import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import assetRopaApi from 'config/api/assetRopa/assetRopa';
import type { OwnerAssetRopaListResponse, OwnerAssetRopaMetrics } from 'config/api/assetRopa/assetRopa.types';
import {
  assetRopaReviewRegisterFiltersToQueryParams,
  assetRopaReviewRoundFiltersToQueryParams,
} from 'config/api/assetRopa/assetRopaFilterMappers';
import type { OwnerWorkspaceMetrics, SoftDeleteProcessingActivityInput } from 'config/api/assetRopa/assetRopaWorkspace.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import ropaReviewsApi from 'config/api/ropaReviews/ropaReviews';
import type { ActiveRopaReviewResponse, AssetRopaReview } from 'config/api/ropaReviews/ropaReviews.types';
import type { AssetRopaPageVariant } from 'pages/ProcessingActivities/_shared/assetRopaCapabilities';
import { catchHttpStatuses, isUnavailableQueryResult } from 'pages/ProcessingActivities/_shared/queryUnavailable';

import type { AssetRopaReviewFilters } from './assetRopaReview.types';
import { buildAssetRopaReviewFilterOptions, filterAssetRopaReviews } from './assetRopaReviewFilters';
import { mapOrganizationWorkspaceAssetToReview } from './mapOrganizationWorkspaceAssetToReview';

type UseAssetRopaReviewPageOptions = {
  enabled?: boolean;
  filters: AssetRopaReviewFilters;
  organizationId?: number;
  variant?: AssetRopaPageVariant;
};

const buildOwnerActiveReview = (
  ownerList: OwnerAssetRopaListResponse | undefined,
  metrics: OwnerAssetRopaMetrics | null | undefined,
): ActiveRopaReviewResponse | null => {
  if (!ownerList?.activeReview) {
    return null;
  }

  const resolvedMetrics = metrics !== undefined ? metrics : ownerList.metrics;

  return {
    review: {
      id: ownerList.activeReview.id,
      status: ownerList.activeReview.status,
      initiatedAt: ownerList.activeReview.initiatedAt,
      deadline: ownerList.activeReview.deadline,
      reviewInstructions: ownerList.activeReview.reviewInstructions,
      initiatedByMembershipId: ownerList.activeReview.initiatedByMembershipId,
      initiatedByDisplayName: ownerList.activeReview.initiatedByDisplayName,
    },
    metrics: resolvedMetrics,
    actions: {
      canEditDetails: false,
      canCompleteReview: false,
      canCancelReview: false,
    },
  };
};

const useAssetRopaReviewPage = ({ enabled = true, filters, organizationId, variant = 'admin' }: UseAssetRopaReviewPageOptions) => {
  const queryClient = useQueryClient();
  const isOwnerVariant = variant === 'owner';
  const registerFilterParams = assetRopaReviewRegisterFiltersToQueryParams(filters);
  const reviewRoundFilterParams = assetRopaReviewRoundFiltersToQueryParams(filters);
  const canFetch = Boolean(enabled && organizationId);

  /**
   * Owner ROPA review is scoped exclusively through asset-ropa/mine* endpoints.
   * Do not call admin ropa-reviews/.../assets/ and filter client-side.
   */
  const ownerMineQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE, organizationId, registerFilterParams],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await assetRopaApi.getMine(organizationId as number, registerFilterParams);
        return data;
      }, [403]),
    {
      enabled: Boolean(canFetch && isOwnerVariant),
      retry: false,
      keepPreviousData: true,
    },
  );

  const ownerMetricsQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE_METRICS, organizationId],
    async () => {
      const { data } = await assetRopaApi.getMineMetrics(organizationId as number);
      return data;
    },
    {
      enabled: Boolean(canFetch && isOwnerVariant),
      retry: false,
      keepPreviousData: true,
    },
  );

  const organizationRegisterQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_ORG, organizationId, registerFilterParams],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await assetRopaApi.getOrganizationWorkspace(organizationId as number, registerFilterParams);
        return data;
      }, [403]),
    {
      enabled: Boolean(canFetch && !isOwnerVariant),
      retry: false,
      keepPreviousData: true,
    },
  );

  const activeReviewQuery = useQuery(
    [QUERY_KEYS.ROPA_REVIEWS_ACTIVE, organizationId, variant],
    async () =>
      catchHttpStatuses(async () => {
        const { data } = await ropaReviewsApi.getActiveReview(organizationId as number);
        return data;
      }, [403]),
    {
      enabled: Boolean(canFetch && !isOwnerVariant),
      retry: false,
    },
  );

  const ownerMineData = isUnavailableQueryResult(ownerMineQuery.data) ? undefined : ownerMineQuery.data;
  const organizationRegisterData = isUnavailableQueryResult(organizationRegisterQuery.data) ? undefined : organizationRegisterQuery.data;
  const activeReviewData = isUnavailableQueryResult(activeReviewQuery.data) ? undefined : activeReviewQuery.data;

  const reviewRoundId = isOwnerVariant ? ownerMineData?.activeReview?.id : activeReviewData?.review.id;
  const hasAdminActiveReview = Boolean(!isOwnerVariant && reviewRoundId);

  const reviewAssetsQuery = useQuery(
    [QUERY_KEYS.ROPA_REVIEWS_ASSETS, organizationId, reviewRoundId, reviewRoundFilterParams],
    async () => {
      const { data } = await ropaReviewsApi.getAssetReviews(organizationId as number, reviewRoundId as number, reviewRoundFilterParams);
      return data;
    },
    {
      enabled: Boolean(canFetch && hasAdminActiveReview),
      retry: false,
      keepPreviousData: true,
    },
  );

  let activeReview: ActiveRopaReviewResponse | null = null;
  if (isOwnerVariant) {
    const ownerReviewMetrics = ownerMetricsQuery.data !== undefined ? ownerMetricsQuery.data.reviewMetrics : undefined;
    activeReview = buildOwnerActiveReview(ownerMineData, ownerReviewMetrics);
  } else if (activeReviewData) {
    activeReview = {
      ...activeReviewData,
      metrics: organizationRegisterData?.reviewMetrics ?? activeReviewData.metrics,
    };
  }

  const workspaceMetrics: OwnerWorkspaceMetrics | undefined = isOwnerVariant ? undefined : organizationRegisterData?.workspaceMetrics;

  const registerAssetReviews = useMemo(
    () => (organizationRegisterData?.assets ?? []).map(mapOrganizationWorkspaceAssetToReview),
    [organizationRegisterData?.assets],
  );

  const allAssetReviews = useMemo((): AssetRopaReview[] => {
    if (isOwnerVariant) {
      return (ownerMineData?.assets ?? []).map(mapOrganizationWorkspaceAssetToReview);
    }

    if (hasAdminActiveReview) {
      return reviewAssetsQuery.data ?? [];
    }

    return registerAssetReviews;
  }, [hasAdminActiveReview, isOwnerVariant, ownerMineData?.assets, registerAssetReviews, reviewAssetsQuery.data]);

  /**
   * Admin with an active review round: BE filters everything via review-round params.
   * Admin without a review / owner via mine: register params go to BE; review-only filters
   * (reviewStatus, operationalOwner, warningState) are applied client-side; DPIA filters are server-side.
   */
  const filteredAssetReviews = useMemo(() => {
    if (hasAdminActiveReview) {
      return allAssetReviews;
    }

    return filterAssetRopaReviews(allAssetReviews, filters);
  }, [allAssetReviews, filters, hasAdminActiveReview]);

  const filterOptions = useMemo(() => buildAssetRopaReviewFilterOptions(allAssetReviews), [allAssetReviews]);

  const invalidateReviewQueries = async () => {
    await queryClient.invalidateQueries([QUERY_KEYS.ROPA_REVIEWS_ACTIVE, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ROPA_REVIEWS_ASSETS, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_ORG, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE_METRICS, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_MINE_DETAIL, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_AVAILABLE_PROCESSING_ACTIVITIES, organizationId]);
    await queryClient.invalidateQueries([QUERY_KEYS.ASSET_ROPA_WORKSPACE, organizationId]);
  };

  const acceptProcessingReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_ACCEPT_PROCESSING_REVIEW,
    async (reviewRowId: number) => {
      await ropaReviewsApi.acceptAssetProcessingActivityReview(organizationId as number, reviewRowId);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const requestClarificationMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_REQUEST_CLARIFICATION,
    async ({ comment, reviewRowId }: { comment: string; reviewRowId: number }) => {
      await ropaReviewsApi.requestAssetProcessingActivityReviewClarification(organizationId as number, reviewRowId, { comment });
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const bulkAcceptMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_BULK_ACCEPT_SUBMITTED,
    async (assetReviewId: number) => {
      await ropaReviewsApi.bulkAcceptSubmitted(organizationId as number, reviewRoundId as number, assetReviewId);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const addProcessingActivityMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_ADD_PROCESSING_ACTIVITY,
    async ({ assetRopaId, processingActivityId }: { assetRopaId: number; processingActivityId: number }) => {
      const { data } = await assetRopaApi.addProcessingActivity(organizationId as number, assetRopaId, { processingActivityId });
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const softDeleteMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_SOFT_DELETE_APA,
    async ({ apaId, assetRopaId, payload }: { apaId: number; assetRopaId: number; payload: SoftDeleteProcessingActivityInput }) => {
      const { data } = await assetRopaApi.softDeleteProcessingActivity(organizationId as number, assetRopaId, apaId, payload);
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const updateReviewDetailsMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_UPDATE_DETAILS,
    async (payload: { deadline: string; reviewInstructions: string }) => {
      await ropaReviewsApi.updateReviewDetails(organizationId as number, reviewRoundId as number, payload);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const completeReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_COMPLETE,
    async (completionComment: string) => {
      const { data } = await ropaReviewsApi.completeReview(organizationId as number, reviewRoundId as number, {
        completionComment,
      });
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const cancelReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_CANCEL,
    async (cancellationComment: string) => {
      const { data } = await ropaReviewsApi.cancelReview(organizationId as number, reviewRoundId as number, {
        cancellationComment,
      });
      return data;
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const confirmNoProcessingActivitiesMutation = useMutation(
    MUTATION_KEYS.ASSET_ROPA_CONFIRM_NO_PROCESSING_ACTIVITIES,
    async (assetRopaId: number) => {
      await assetRopaApi.confirmNoProcessingActivities(organizationId as number, assetRopaId);
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const cancelIncompleteAssetReviewMutation = useMutation(
    MUTATION_KEYS.ROPA_REVIEWS_CANCEL_INCOMPLETE_ASSET_REVIEW,
    async ({ assetReviewId, cancellationComment }: { assetReviewId: number; cancellationComment: string }) => {
      await ropaReviewsApi.cancelIncompleteAssetReview(organizationId as number, reviewRoundId as number, assetReviewId, {
        cancellationComment,
      });
    },
    {
      onSuccess: invalidateReviewQueries,
    },
  );

  const adminListError = organizationRegisterQuery.isError || activeReviewQuery.isError || reviewAssetsQuery.isError;
  const listError = isOwnerVariant ? ownerMineQuery.isError : adminListError;

  const isForbidden = Boolean(
    (isUnavailableQueryResult(ownerMineQuery.data) && ownerMineQuery.data.status === 403) ||
      (isUnavailableQueryResult(organizationRegisterQuery.data) && organizationRegisterQuery.data.status === 403) ||
      (isUnavailableQueryResult(activeReviewQuery.data) && activeReviewQuery.data.status === 403),
  );

  const isLoading = isOwnerVariant
    ? ownerMineQuery.isLoading
    : activeReviewQuery.isLoading || organizationRegisterQuery.isLoading || (hasAdminActiveReview && reviewAssetsQuery.isLoading);

  const isError = listError;

  const assetReviewsQuery = useMemo(() => {
    if (isOwnerVariant) {
      return ownerMineQuery;
    }

    if (hasAdminActiveReview) {
      return reviewAssetsQuery;
    }

    return organizationRegisterQuery;
  }, [hasAdminActiveReview, isOwnerVariant, organizationRegisterQuery, ownerMineQuery, reviewAssetsQuery]);

  return {
    acceptProcessingReviewMutation,
    activeReview,
    addProcessingActivityMutation,
    allAssetReviews,
    assetReviewsQuery,
    bulkAcceptMutation,
    cancelReviewMutation,
    cancelIncompleteAssetReviewMutation,
    completeReviewMutation,
    confirmNoProcessingActivitiesMutation,
    filterOptions,
    filteredAssetReviews,
    invalidateReviewQueries,
    isError,
    isForbidden,
    isLoading,
    refetch: async () => {
      if (isOwnerVariant) {
        await Promise.all([ownerMineQuery.refetch(), ownerMetricsQuery.refetch()]);
        return;
      }

      await Promise.all([
        organizationRegisterQuery.refetch(),
        activeReviewQuery.refetch(),
        ...(hasAdminActiveReview ? [reviewAssetsQuery.refetch()] : []),
      ]);
    },
    requestClarificationMutation,
    reviewRoundId,
    workspaceMetrics,
    softDeleteMutation,
    updateReviewDetailsMutation,
    variant,
  };
};

export default useAssetRopaReviewPage;
